// @flow

import { ThemeProvider, css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link, withPrefix } from 'gatsby'
import * as React from 'react'

import { useSession } from '@toggl/auth'
import { colors, media, themes } from '@toggl/style'
import {
  Button,
  Footer,
  Global,
  Header,
  Hire,
  IconCaretRight,
  IconDown,
  IconUp,
  Navigation,
  Plan,
  Responsive,
  Track,
} from '@toggl/ui'
import { useUploads } from 'components'
import typeof HeaderAnnouncement from 'components/HeaderAnnouncement'

// import { keyframes } from '@emotion/core'
import { navigation } from '../../../data/global/layout.yml'

import { LayoutRoot } from './LayoutRoot'

declare var process: WebProcess

type Props = {
  ...React.ElementProps<typeof LayoutRoot>,
  announcement?: React.Element<HeaderAnnouncement>,
  children?: React.Node,
  background?: string,
}

export const LayoutGlobal = ({
  children,
  announcement,
  background,
  ...rest
}: Props): React.Element<typeof LayoutRoot> => {
  const [contentKey, _setContentKey] = React.useState<
    null | 'login' | 'products'
  >(null)
  const [hasTrackSession] = useSession()
  const uploads = useUploads({
    products: navigation.products.items?.map((i) => i.image),
    login: navigation.login.items?.map((i) => i.image),
  })

  const setContentKey = (val) => {
    if (contentKey === val) {
      return _setContentKey(null)
    }

    _setContentKey(val)
  }

  return (
    <LayoutRoot {...rest}>
      <ThemeProvider theme={themes.global}>
        <Header>
          {announcement ? announcement : null}
          <Navigation.Header
            onToggle={(isOpen) => {
              if (!isOpen) {
                setContentKey(null)
              }
            }}
            open={!!contentKey}
            background={background}
            logo={<Logo open={!!contentKey} />}
            logoWidth={['64px', '85px']}
            login={
              <Button.HeaderButtonWrapper
                ariaExpanded={contentKey === 'login'}
                onClick={() => setContentKey('login')}
                role="button"
                tabIndex={0}
              >
                Log in
                <Responsive.DesktopOnly>
                  {contentKey === 'login' ? (
                    <IconUp />
                  ) : (
                    <IconDown dark={!!contentKey} />
                  )}
                </Responsive.DesktopOnly>
                <Responsive.MobileOnly>
                  <IconCaretRight />
                </Responsive.MobileOnly>
              </Button.HeaderButtonWrapper>
            }
            Cta={() => (
              <Button.Primary
                onClick={() => setContentKey('products')}
                reducedDevicePadding
              >
                Try Toggl
              </Button.Primary>
            )}
          >
            <Button.HeaderButtonWrapper
              ariaExpanded={contentKey === 'products'}
              onClick={() => setContentKey('products')}
              role="button"
              tabIndex={0}
            >
              Products
              <Responsive.DesktopOnly>
                {contentKey === 'products' ? (
                  <IconUp />
                ) : (
                  <IconDown dark={!!contentKey} />
                )}
              </Responsive.DesktopOnly>
              <Responsive.MobileOnly>
                <IconCaretRight />
              </Responsive.MobileOnly>
            </Button.HeaderButtonWrapper>
            <a
              href={'https://toggl.com/blog/'}
              rel="noreferrer"
              target="_blank"
            >
              Blog
            </a>
            <Link to={'/mission/'}>Our mission</Link>
            <Link to={'/jobs/'}>Working at Toggl</Link>
          </Navigation.Header>
          <Navigation.ExpandedMenu
            logoWidth={['64px', '85px']}
            onClose={() => setContentKey(null)}
          >
            {contentKey && (
              <Contents
                kind={contentKey}
                uploads={uploads}
                hasTrackSession={hasTrackSession}
              />
            )}
          </Navigation.ExpandedMenu>
        </Header>
        {children}
        <Footer.Wrapper
          Link={Link}
          logo={<IconGlobal />}
          products={[
            <>
              <TrackLink to={'/track/'} key="track">
                <Track />
                <span>Effortless time tracking</span>
              </TrackLink>
            </>,
            <>
              <PlanLink href={withPrefix('/plan/')} key="plan">
                <Plan />
                <span>Beautifully simple project planning</span>
              </PlanLink>
            </>,
            <>
              <HireLink href={withPrefix('/hire/')} key="hire">
                <Hire />
                <span>Intelligent candidate-screening</span>
              </HireLink>
            </>,
          ]}
        />
      </ThemeProvider>
    </LayoutRoot>
  )
}

const Contents = ({ kind, uploads, hasTrackSession }) => {
  switch (kind) {
    case 'products':
      return <Products uploads={uploads} />
    case 'login':
      return <Login uploads={uploads} hasTrackSession={hasTrackSession} />
    case null:
      return null
    default:
      ;(kind: empty)
      throw new Error()
  }
}

const Products = ({ uploads }) => (
  <Navigation.MenuContent
    title={navigation.products.title}
    description={navigation.products.subtitle}
  >
    <Navigation.ProductsGroup>
      {navigation.products.items?.map((item, index) => {
        return (
          <Link key={index} to={item.url}>
            <Navigation.Product
              key={index}
              img={uploads.products[index]}
              title={item.title}
              description={item.subtitle}
              mediaWidth="284"
              mediaHeight="178"
            />
          </Link>
        )
      })}
    </Navigation.ProductsGroup>
  </Navigation.MenuContent>
)

const Login = ({ uploads, hasTrackSession }) => (
  <Navigation.MenuContent
    title={navigation.login.title}
    description={navigation.login.subtitle}
  >
    <Navigation.ProductsGroup>
      {navigation.login.items?.map((item, index) => {
        return (
          <Link
            key={index}
            to={
              item['login-link'] === 'track' && hasTrackSession
                ? process.env.GATSBY_TRACK_APP_URL
                : item.url
            }
          >
            <Navigation.Product
              img={uploads.login[index]}
              title={item.title}
              description={item.subtitle}
              mediaWidth="284"
              mediaHeight="178"
            />
          </Link>
        )
      })}
    </Navigation.ProductsGroup>
  </Navigation.MenuContent>
)

const IconGlobal = () => (
  <div
    css={css`
      width: 85px;
      svg {
        width: 100%;
      }
    `}
  >
    <Global />
  </div>
)

const Logo = ({ open }) => (
  <Link to={'/'}>
    <Global
      css={css`
        transition-property: fill;
        transition: 300ms;
        transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);

        fill: ${colors.darkPurple};

        ${media.mq[1]} {
          fill: ${open ? colors.darkPurple : colors.yellow};
          transition-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
      `}
    />
  </Link>
)

const TrackLink = styled(Link)`
  &:hover {
    color: ${colors.pink};
  }
`
const PlanLink = styled.a`
  &:hover {
    color: ${colors.violet};
  }
`
const HireLink = styled.a`
  &:hover span {
    color: ${colors.orange};
  }
`
